import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-endometrial-add-on-bundle"),
  kind: "test",
  tags: ["testing", "her", "ivf", "fet", "pre-cycle"],
  title: "Endometrial Add-on Bundle",
  subtitle:
    "Receptivity, microbiome, and chronic endometritis tests in one biopsy",
  icon: "🧬",
  body: {
    testName: "Endometrial Add-on Bundle",
    category: "biopsy",
    evidenceStrength: "debunked",
    whatItMeasures:
      "Combines endometrial receptivity timing, microbiome analysis, and chronic endometritis testing from one biopsy.\n\nEndomeTRIO is the Igenomix-branded bundle; ER-Complete is a CooperSurgical bundle combining ERPeak and ERBiome.\n\nBundling can produce more data, but it does not fix the evidence gap: receptivity testing has not improved live birth in high-quality trials, and NICE found no eligible effectiveness evidence for microbiological analysis tests such as EMMA or ALICE.",
    howItWorks:
      "A clinician takes one endometrial biopsy, usually in a mock transfer cycle. The lab runs some combination of receptivity timing, microbiome composition, and chronic endometritis analysis from the sample. The report may suggest changing transfer timing, using antibiotics or probiotics, or repeating the biopsy. Treat this as an unproven clinical add-on unless there is a specific reason and a clear plan for how the result would change care.",
    quickFacts: [
      { kind: "sample", value: "Endometrial tissue biopsy" },
      { kind: "time", value: "5-10 min biopsy" },
      { kind: "pain", value: "Cramping during and shortly after" },
      { kind: "anesthesia", value: "Usually none" },
      { kind: "prep", value: "No fasting usually; ask about ibuprofen" },
      { kind: "results", value: "2-3 weeks" },
    ],
    resultTypes: [
      {
        label: "All components not flagged",
        meaning:
          "The receptivity result is receptive, microbiome testing is not flagged, and chronic endometritis testing does not detect a concerning finding.",
        whatToExpect:
          "This may feel reassuring, but it does not guarantee implantation and does not prove the bundle improves live birth.",
      },
      {
        label: "ERA non-receptive",
        meaning:
          "The receptivity portion classifies the sample as pre-receptive or post-receptive.",
        whatToExpect:
          "The report may suggest changing progesterone exposure or transfer timing. High-quality trials have not shown routine live birth benefit from this strategy.",
      },
      {
        label: "Microbiome or inflammation flagged",
        meaning: "The microbiome or chronic endometritis portion is flagged.",
        whatToExpect:
          "Antibiotics, probiotics, or repeat testing may be suggested. Ask which component is driving the plan and whether the change is evidence-based.",
      },
      {
        label: "Mixed or inconclusive",
        meaning:
          "One or more components are unclear, low-biomass, or not aligned with the rest of the report.",
        whatToExpect:
          "This can lead to repeat biopsy or delay. Ask what would happen if you ignored the unclear component and proceeded with standard care.",
      },
    ],
    expectations: {
      canExpect: [
        "One biopsy used for receptivity timing plus microbiome and chronic endometritis bacterial analysis",
        "A bundled report that may suggest transfer timing changes, antibiotics, probiotics, or repeat biopsy",
        "A delay before transfer because the biopsy is usually done in a mock cycle",
      ],
      shouldNotExpect: [
        "Bundling receptivity, microbiome, and chronic endometritis tests does not create stronger evidence",
        "Major independent guidance does not recommend routine use",
        "An abnormal report does not prove it explains implantation failure",
      ],
    },
    considerations: [
      "Ask which part of the bundle is being recommended and why: receptivity timing, microbiome, chronic endometritis, or all three",
      "Ask whether a simpler and better-supported uterine evaluation has already been completed",
      "Ask how your plan changes if one component is abnormal and whether that change is evidence-based",
      "Avoid paying for the bundle when the recommendation is vague or routine",
    ],
    bestFor: [
      "Research setting",
      "Highly selected recurrent implantation failure cases after standard workup",
      "Not recommended as a routine IVF add-on",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "Receptivity-timed transfer did not improve live birth compared with standard timing in a large randomized trial",
        magnitude: "58.5% vs 61.9% live birth in the JAMA RCT",
        source: "Doyle et al., JAMA 2022",
      },
      {
        direction: "negative",
        outcome:
          "NICE found no eligible effectiveness evidence for EMMA or ALICE and no important benefit for ERA",
        source: "NICE NG257 evidence review, 2026",
      },
      {
        direction: "negative",
        outcome:
          "HFEA rates endometrial receptivity testing red for most fertility patients",
        source: "HFEA treatment add-ons guidance, 2023",
      },
    ],
    soldAs: [
      {
        name: "EndomeTRIO",
        company: "Igenomix",
        description:
          "A branded bundle that combines ERA, EMMA, and ALICE from one endometrial biopsy.",
        evidenceSummary:
          "Bundling adds more test outputs, but it does not resolve the evidence gaps for receptivity timing or microbiome testing.",
        linkedTestId: makeId(
          "test-endometrial-add-on-bundle-products-endometrio",
        ),
      },
      {
        name: "ER-Complete",
        company: "CooperSurgical",
        description:
          "A branded bundle that combines ERPeak receptivity testing with ERBiome microbiome testing.",
        evidenceSummary:
          "Should be interpreted through both the receptivity-testing and microbiome-testing evidence gaps.",
        linkedTestId: makeId(
          "test-endometrial-add-on-bundle-products-er-complete",
        ),
      },
    ],
    headlineStat: "Bundle not supported",
    turnaroundTime: "2-3 weeks",
    costRange: { low: 1200, high: 2200 },
    atHome: false,
    clinicRequired: true,
    cycleDay:
      "Usually timed like a receptivity test in a mock transfer cycle: often P+5 in a medicated cycle or LH/hCG+7 in a natural cycle, but the exact protocol depends on the lab and clinic.",
    dataPoints: [],
    section: "implantation",
  },
  sources: [
    {
      title:
        "Fertility problems: assessment and treatment, evidence review D, endometrial receptivity testing",
      journal: "NICE NG257",
      year: 2026,
      url: "https://www.nice.org.uk/guidance/ng257/evidence/d-endometrial-receptivity-testing-pdf-15664473329",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Effect of timing by endometrial receptivity testing vs standard timing of frozen embryo transfer on live birth",
      journal: "JAMA",
      year: 2022,
      url: "https://jamanetwork.com/journals/jama/fullarticle/2799154",
      evidenceLevel: "rct",
    },
    {
      title: "ERA-EMMA-ALICE Manual EndomeTRIO",
      journal: "Igenomix technical manual",
      year: 2022,
      url: "https://www.igenomix.co.uk/wp-content/uploads/sites/7/2022/03/ERA-EMMA-ALICE-Manual-EndomeTRIO.pdf",
      evidenceLevel: "expert-opinion",
    },
    {
      title: "ER-Complete endometrial receptivity and microbiome test",
      journal: "CooperSurgical product information",
      year: 2026,
      url: "https://www.coopersurgical.com/patient-article/er-complete-test-for-patients/",
      evidenceLevel: "expert-opinion",
    },
    {
      title:
        "Good practice recommendations on add-ons in reproductive medicine",
      authors: "ESHRE Add-ons working group; Lundin K, Bentzen JG, et al.",
      journal: "Human Reproduction",
      year: 2023,
      url: "https://pubmed.ncbi.nlm.nih.gov/37747409/",
      evidenceLevel: "guideline",
    },
    {
      title: "Treatment add-ons with limited evidence (HFEA traffic-light)",
      authors: "Human Fertilisation and Embryology Authority",
      journal: "HFEA",
      year: 2023,
      url: "https://www.hfea.gov.uk/treatments/treatment-add-ons/",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 4,
};

registerBlock(test);

const endomeTrioProduct: ContentBlock<"test"> = {
  id: makeId("test-endometrial-add-on-bundle-products-endometrio"),
  kind: "test",
  tags: ["testing", "her", "ivf", "fet", "pre-cycle"],
  title: "EndomeTRIO",
  subtitle: "Commercial test profile for an endometrial add-on bundle",
  icon: "🧬",
  body: {
    testName: "EndomeTRIO (ERA + EMMA + ALICE)",
    category: "biopsy",
    evidenceStrength: "debunked",
    isCommercialProductProfile: true,
    commercialProductProfile: {
      categoryTestId: makeId("test-endometrial-add-on-bundle"),
      company: "Igenomix",
      productUrl:
        "https://www.igenomix.co.uk/wp-content/uploads/sites/7/2022/03/ERA-EMMA-ALICE-Manual-EndomeTRIO.pdf",
      marketedNames: ["EndomeTRIO", "ERA + EMMA + ALICE"],
      availability: [
        {
          region: "Patient access",
          detail:
            "Usually ordered through a fertility clinic after one timed endometrial biopsy; availability depends on the clinic and Igenomix lab route.",
        },
      ],
    },
    whatItMeasures:
      "EndomeTRIO is the Igenomix-branded bundle that combines ERA receptivity timing, EMMA microbiome analysis, and ALICE chronic endometritis-associated bacteria testing.",
    howItWorks: test.body.howItWorks,
    quickFacts: test.body.quickFacts,
    resultTypes: test.body.resultTypes,
    expectations: test.body.expectations,
    considerations: test.body.considerations,
    bestFor: test.body.bestFor,
    keyFindings: test.body.keyFindings,
    headlineStat: test.body.headlineStat,
    turnaroundTime: test.body.turnaroundTime,
    costRange: test.body.costRange,
    atHome: false,
    clinicRequired: true,
    cycleDay: test.body.cycleDay,
    dataPoints: [],
    section: "implantation",
  },
  sources: test.sources,
  readTimeMinutes: 4,
};

const erCompleteProduct: ContentBlock<"test"> = {
  id: makeId("test-endometrial-add-on-bundle-products-er-complete"),
  kind: "test",
  tags: ["testing", "her", "ivf", "fet", "pre-cycle"],
  title: "ER-Complete",
  subtitle: "Commercial test profile for an endometrial add-on bundle",
  icon: "🧬",
  body: {
    testName: "ER-Complete",
    category: "biopsy",
    evidenceStrength: "debunked",
    isCommercialProductProfile: true,
    commercialProductProfile: {
      categoryTestId: makeId("test-endometrial-add-on-bundle"),
      company: "CooperSurgical",
      productUrl:
        "https://www.coopersurgical.com/patient-article/er-complete-test-for-patients/",
      marketedNames: ["ER-Complete", "ERPeak + ERBiome"],
      availability: [
        {
          region: "Patient access",
          detail:
            "Usually ordered through a fertility clinic after one timed endometrial biopsy; availability depends on the clinic and CooperSurgical genetics workflow.",
        },
      ],
    },
    whatItMeasures:
      "ER-Complete is CooperSurgical's branded bundle that combines ERPeak receptivity testing with ERBiome endometrial microbiome testing.",
    howItWorks: test.body.howItWorks,
    quickFacts: test.body.quickFacts,
    resultTypes: test.body.resultTypes,
    expectations: test.body.expectations,
    considerations: [
      "Ask which component is driving the recommendation: ERPeak, ERBiome, or the bundle.",
      "Ask what direct ER-Complete evidence the clinic is relying on.",
      "Ask how the plan changes if only one component is abnormal or inconclusive.",
    ],
    bestFor: test.body.bestFor,
    keyFindings: test.body.keyFindings,
    headlineStat: test.body.headlineStat,
    turnaroundTime: test.body.turnaroundTime,
    costRange: test.body.costRange,
    atHome: false,
    clinicRequired: true,
    cycleDay: test.body.cycleDay,
    dataPoints: [],
    section: "implantation",
  },
  sources: test.sources,
  readTimeMinutes: 4,
};

registerBlock(endomeTrioProduct);
registerBlock(erCompleteProduct);

registerRelationship({
  from: makeId("test-endometrial-add-on-bundle"),
  to: makeId("test-endometrial-add-on-bundle-products-endometrio"),
  type: "related-to",
  label: "Sold as",
});

registerRelationship({
  from: makeId("test-endometrial-add-on-bundle"),
  to: makeId("test-endometrial-add-on-bundle-products-er-complete"),
  type: "related-to",
  label: "Sold as",
});

for (const testId of [
  "test-endometrial-receptivity-testing",
  "test-endometrial-microbiome-testing",
  "test-chronic-endometritis-testing",
]) {
  registerRelationship({
    from: makeId("test-endometrial-add-on-bundle"),
    to: makeId(testId),
    type: "related-to",
    label: "Included test",
  });
}
